import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Pencil, Plus, Trash2, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { trophiesService, type ClassOwner } from '../../services/trophies';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import { useGymAdmin } from '../context/gym-admin-context';
import type { TrophyTeam } from '../../types/trophy';

/** What the disband dialog means by "leave them without a squad". */
const NO_TEAM = '__none__';

/**
 * The circuit's squads — a club, a region's selection, a gym's competition team.
 *
 * They live on the trophy rather than on each stage because a squad enters the
 * season, not one weekend: an entry made at any stage can point at one and it
 * still means the same thing at the next. A squad is also what the team managers
 * are named on, which is why the desk may add one: whoever types the entries is
 * who meets a club nobody has written down yet.
 *
 * Disbanding one does not remove the athletes in it. They either move to another
 * squad — the way out of the same club written down twice — or stay entered and
 * climb for nobody, which is why the dialog says how many people that will be.
 * Renaming and disbanding stay the admins', since both move other people's
 * entries around.
 */
export default function TeamsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { location: trophy, loading: trophyLoading, slug, locationType } = useCurrentLocation();
  const { hasAnyRole } = useGymAdmin();
  // Writing a squad is the desk's; correcting and folding them is not.
  const canCorrect = hasAnyRole(['admin']);
  // A gym's own event keeps its own list, on the same endpoints under its
  // own path; the menu never shows this page on a stage.
  const owner: ClassOwner = locationType === 'competition' ? 'competition' : 'trophy';

  const [teams, setTeams] = useState<TrophyTeam[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  // The squad the disband dialog is open for, and where its people should go.
  const [disbanding, setDisbanding] = useState<TrophyTeam | null>(null);
  const [moveTo, setMoveTo] = useState<string>(NO_TEAM);

  const load = useCallback(
    async (trophySlug: string) => {
      setListLoading(true);
      try {
        setTeams(await trophiesService.listTeams(trophySlug, owner));
      } catch (error) {
        toast({
          title: t('gymAdmin.teams.loadError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast, owner]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  const reportFailure = (error: unknown) =>
    toast({
      title: t('gymAdmin.teams.saveError'),
      description: error instanceof Error ? error.message : t('errors.unexpectedError'),
      variant: 'destructive',
    });

  const handleCreate = async () => {
    if (!slug || !newName.trim()) return;
    setSaving(true);
    try {
      const created = await trophiesService.createTeam(slug, newName.trim(), owner);
      setTeams((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
    } catch (error) {
      reportFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (team: TrophyTeam) => {
    if (!slug || !editingName.trim()) return;
    setBusyId(team.id);
    try {
      const updated = await trophiesService.updateTeam(slug, team.id, editingName.trim(), owner);
      setTeams((current) =>
        current
          .map((c) => (c.id === updated.id ? updated : c))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingId(null);
    } catch (error) {
      reportFailure(error);
    } finally {
      setBusyId(null);
    }
  };

  const handleDisband = async () => {
    if (!slug || !disbanding) return;
    const target = moveTo === NO_TEAM ? null : moveTo;

    setBusyId(disbanding.id);
    try {
      await trophiesService.deleteTeam(slug, disbanding.id, owner, target);
      setTeams((current) =>
        current
          .filter((c) => c.id !== disbanding.id)
          // The people moved are now the other squad's, so its count has to say
          // so without a second round trip.
          .map((c) =>
            target && c.id === target
              ? { ...c, entries_count: c.entries_count + disbanding.entries_count }
              : c
          )
      );
      setDisbanding(null);
    } catch (error) {
      reportFailure(error);
    } finally {
      setBusyId(null);
    }
  };

  if (trophyLoading) return <Skeleton className="h-96 w-full max-w-3xl" />;
  if (!trophy || !slug) return null;

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.teams.title')}</CardTitle>
          <CardDescription>{t('gymAdmin.teams.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {listLoading && <Skeleton className="h-24 w-full" />}

          {!listLoading && teams.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('gymAdmin.teams.empty')}
            </p>
          )}

          {!listLoading &&
            teams.map((team) => (
              <div
                key={team.id}
                className="flex items-center gap-3 rounded-lg border p-3"
                data-testid="team-row"
              >
                {canCorrect && editingId === team.id ? (
                  <>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename(team)}
                      aria-label={t('gymAdmin.teams.name')}
                      autoFocus
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRename(team)}
                      disabled={busyId === team.id}
                      title={t('common.save')}
                    >
                      {busyId === team.id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Check className="size-4" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditingId(null)}
                      title={t('common.cancel')}
                    >
                      <X className="size-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 truncate font-medium">{team.name}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="size-3" aria-hidden />
                      {team.entries_count}
                    </span>
                    {canCorrect && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(team.id);
                            setEditingName(team.name);
                          }}
                          title={t('gymAdmin.teams.rename')}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setDisbanding(team);
                            setMoveTo(NO_TEAM);
                          }}
                          disabled={busyId === team.id}
                          title={t('gymAdmin.teams.delete')}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            ))}

          <div className="flex gap-2 pt-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder={t('gymAdmin.teams.namePlaceholder')}
              aria-label={t('gymAdmin.teams.name')}
            />
            <Button onClick={handleCreate} disabled={!newName.trim() || saving} className="gap-2">
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t('gymAdmin.teams.add')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Disbanding, with the one question that matters: where do its people go.
          Folding it into another squad is what a list the desk may add to needs,
          since the same club gets written down twice sooner or later. */}
      <Dialog open={disbanding !== null} onOpenChange={(open) => !open && setDisbanding(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('gymAdmin.teams.disbandTitle', { name: disbanding?.name ?? '' })}</DialogTitle>
            <DialogDescription>
              {t('gymAdmin.teams.disbandDescription', { count: disbanding?.entries_count ?? 0 })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="team-move-to">{t('gymAdmin.teams.moveTo')}</Label>
            <Select value={moveTo} onValueChange={setMoveTo}>
              <SelectTrigger id="team-move-to">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TEAM}>{t('gymAdmin.teams.moveToNobody')}</SelectItem>
                {teams
                  .filter((team) => team.id !== disbanding?.id)
                  .map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisbanding(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisband}
              disabled={busyId === disbanding?.id}
              className="gap-2"
            >
              {busyId === disbanding?.id && <Loader2 className="size-4 animate-spin" />}
              {t('gymAdmin.teams.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
